// Pure helpers for the tabbed "View Result" panel. Everything here reads the
// already-loaded IIRevResultState (the panel fetches it via useGetResultsByRefID),
// so there is no extra network call — this is purely derive-and-present logic.
import type { ResultType } from '../../../constants/global';
import type { ICitizensSitrepContext } from '../../../interface/child-agent';
import type { IIRevResultState, IResult } from '../../../interface/state/IRev';

export type VsrTab = 'sheet' | 'data' | 'sitrep';

export interface IVsrTab {
	key: VsrTab;
	label: string;
	short: string;
}

export const VSR_TABS: IVsrTab[] = [
	{ key: 'sheet', label: 'Result Sheet', short: 'Sheet' },
	{ key: 'data', label: 'Results & Analytics', short: 'Data' },
	{ key: 'sitrep', label: 'Situation Reports', short: 'Reports' },
];

export const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

/** A reference that actually identifies a submission. The read store hands back the zero GUID for
 *  a result it has not projected yet, and the zero GUID is truthy. */
export const isRealRef = (ref?: string | null): boolean => !!ref && ref.trim() !== '' && ref !== ZERO_GUID;

/**
 * True when the read store actually projected this result.
 *
 * `GET .../get-by-referenceid/{ref}` answers **200 with an all-null body** and the zero GUID while
 * the command→query projection lags, so a naive `!!result` renders a grid of em-dashes instead of
 * saying anything useful. Never gate on `result.referenceId` alone.
 *
 * NB: `completion-receipt/model.ts` (`isRealRef` + the `figuresAvailable` expression) still carries
 * its own copy of this rule. Converge the two if either is ever changed.
 */
export const hasProjectedResult = (result: IIRevResultState): boolean =>
	!!(
		isRealRef(result.referenceId) ||
		result.id ||
		result.election?.name ||
		result.results?.length ||
		result.totalValidVotes ||
		result.votersOnRegister
	);

/** Sorted (desc) copy of the party results, nulls filtered. */
export const sortedParties = (result: IIRevResultState): IResult[] =>
	[...(result.results ?? [])].filter(Boolean).sort((a, b) => (b.votes ?? 0) - (a.votes ?? 0));

/** A safe rate in [0,1], or null when the denominator is missing/zero. */
const rate = (numerator?: number, denominator?: number): number | null => {
	if (!denominator || denominator <= 0 || numerator == null) return null;
	return numerator / denominator;
};

export const asPercent = (value: number | null): string => (value == null ? '—' : `${(value * 100).toFixed(1)}%`);

export interface IDerivedKpis {
	turnout: number | null; // accredited / registered
	validVoteRate: number | null; // valid votes / accredited
	rejectionRate: number | null; // rejected / valid votes
	ballotUtilisation: number | null; // used ballots / issued
	tallied: number; // sum of party votes
	mismatch: boolean; // party tally ≠ recorded valid votes
	leader: IResult | null;
	runnerUp: IResult | null;
	leadMargin: number | null; // (leader − runnerUp) / valid votes
}

export const computeKpis = (result: IIRevResultState): IDerivedKpis => {
	const parties = sortedParties(result);
	const tallied = parties.reduce((sum, p) => sum + (p.votes ?? 0), 0);
	const valid = result.totalValidVotes ?? 0;
	const leader = parties[0] ?? null;
	const runnerUp = parties[1] ?? null;
	const leadMargin = leader && valid > 0 ? ((leader.votes ?? 0) - (runnerUp?.votes ?? 0)) / valid : null;

	return {
		turnout: rate(result.accreditedVoters, result.votersOnRegister),
		validVoteRate: rate(result.totalValidVotes, result.accreditedVoters),
		rejectionRate: rate(result.rejectedBallot, result.totalValidVotes),
		ballotUtilisation: rate(result.totalUsedBallotPapers, result.ballotPapersIssuedToPoolingUnit),
		tallied,
		mismatch: valid > 0 && tallied !== valid,
		leader,
		runnerUp,
		leadMargin,
	};
};

/**
 * Data shape for the party-vote column chart. Still the legacy FusionCharts
 * `mscolumn2d` `{ categories, dataset }` contract: the chart itself now renders
 * with Recharts, but keeping the contract meant the port touched one file.
 */
export const buildChartData = (result: IIRevResultState) => {
	const parties = sortedParties(result);
	return {
		categories: [{ category: parties.map((p) => ({ label: p.partyName || `Party ${p.partyId}` })) }],
		dataset: [{ seriesname: 'Votes', data: parties.map((p) => ({ value: String(p.votes ?? 0) })) }],
	};
};

const realCode = (value?: string | null): string | undefined => {
	const v = (value || '').trim();
	return v ? v : undefined;
};

/**
 * Builds the citizens-sitrep hierarchy context from the loaded result. Unlike the
 * child-agent drawer (which only had a PU code), the result payload carries the
 * full ancestry, so this satisfies `canQueryCitizensSitrep` and the citizens
 * source actually returns rows here.
 *
 * Casing matters: the read repository matches the polling unit by CODE
 * (`poolingUnitCode`) but the ward / LGA by NAME (`ward` / `lga`) — so we query
 * with the PU code and the ward/LGA names, exactly the values the create endpoint
 * stores, so an agent's own report round-trips. geoZone/zone are required by the
 * request DTO (ignored by the filter) — any non-empty value passes the gate.
 */
export const buildSitrepContext = (result: IIRevResultState): Partial<ICitizensSitrepContext> => {
	const puCode = realCode(result.pollingUnit?.code);
	const wardName = realCode(result.ward?.name);
	const lgaName = realCode(result.localGovernment?.name);
	const geoZone = realCode(result.geoZone?.name) || realCode(result.geoZone?.code);
	const zone = realCode(result.zone?.name) || realCode(result.zone?.code);

	return {
		poolingUnitCodes: puCode ? [puCode] : [],
		wards: wardName ? [wardName] : [],
		localGovernments: lgaName ? [lgaName] : [],
		geoZones: geoZone ? [geoZone] : [],
		zones: zone ? [zone] : [],
		electionId: result.election?.id || undefined,
	};
};

/** Human label for the agent's collation level (drives header eyebrow + sitrep copy). */
export const levelLabel = (resultType?: ResultType | null): string => {
	switch (resultType) {
		case 'EC8A':
			return 'Polling Unit';
		case 'EC8B':
			return 'Ward';
		case 'EC8C':
			return 'Local Government';
		case 'EC8D':
			return 'State';
		default:
			return 'Result';
	}
};

/** The location context handed to the situation-report form for this entity. */
export interface ISitrepLocationContext {
	electionId: number;
	electionName: string;
	level: string;
	state?: { code?: string; name?: string };
	localGovernment?: { code?: string; name?: string };
	ward?: { code?: string; name?: string };
	pollingUnit?: { code?: string; name?: string };
	geoZone?: { code?: string; name?: string };
	zone?: { code?: string; name?: string };
}

export const buildLocationContext = (result: IIRevResultState, resultType?: ResultType | null): ISitrepLocationContext => ({
	electionId: result.election?.id || 0,
	electionName: result.election?.name || '',
	level: levelLabel(resultType),
	state: result.state,
	localGovernment: result.localGovernment,
	ward: result.ward,
	pollingUnit: result.pollingUnit,
	geoZone: result.geoZone,
	zone: result.zone,
});
